import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Star, Quote, Building2, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

interface Testimonial {
  quote: string;
  author: string;
  position: string;
  company: string;
  avatar: string;
  rating: number;
  logo: string;
  industry: string;
  color: string;
}

const Testimonials: React.FC = () => {
  const testimonials: Testimonial[] = [
    {
      quote: "Les motos électriques Terra ont transformé notre service de livraison. L'autonomie est impressionnante et les coûts d'exploitation sont nettement réduits. Un investissement qui en vaut vraiment la peine.",
      author: "Amadou Diallo",
      position: "Directeur des Opérations",
      company: "Heetch",
      avatar: "https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg",
      rating: 5,
      logo: "/Heetch_Logo.png",
      industry: "Transport",
      color: "from-blue-500 to-blue-600"
    },
    {
      quote: "Grâce à Proxy Emobility, nous avons pu créer une flotte de transport 100% électrique. Le réseau de stations de recharge nous assure une disponibilité maximale.",
      author: "Marie Koné",
      position: "Fondatrice",
      company: "Fleetch",
      avatar: "https://images.pexels.com/photos/2381069/pexels-photo-2381069.jpeg",
      rating: 5,
      logo: "/Fleetch-Blue.png",
      industry: "Logistique",
      color: "from-green-500 to-green-600"
    },
    {
      quote: "La moto Sunu est parfaitement adaptée à nos besoins en zone urbaine. Robuste, fiable et économique, elle nous permet d'assurer nos services de livraison efficacement.",
      author: "Dr. Emmanuel Mensah",
      position: "Coordinateur",
      company: "Yango Delivery",
      avatar: "https://images.pexels.com/photos/2379005/pexels-photo-2379005.jpeg",
      rating: 5,
      logo: "/Yango_Delivery.png",
      industry: "Livraison",
      color: "from-red-500 to-red-600"
    }
  ];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      nextTestimonial();
    }, 8000);
    
    return () => clearInterval(interval);
  }, [currentIndex]);

  const nextTestimonial = () => {
    if (!animating) {
      setAnimating(true);
      setCurrentIndex((prevIndex) => (prevIndex + 1) % testimonials.length);
      setTimeout(() => setAnimating(false), 500);
    }
  };

  const prevTestimonial = () => {
    if (!animating) {
      setAnimating(true);
      setCurrentIndex((prevIndex) => (prevIndex - 1 + testimonials.length) % testimonials.length);
      setTimeout(() => setAnimating(false), 500);
    }
  };

  return (
    <section className="py-12 sm:py-16 md:py-24 bg-gradient-to-b from-white to-primary-50 overflow-hidden relative">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_90%_10%,_rgba(24,153,78,0.05)_0%,_transparent_50%)]"></div>
      
      <div className="container mx-auto px-4 md:px-6 relative">
        <div className="text-center max-w-3xl mx-auto mb-8 sm:mb-12 md:mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-neutral-900 mb-4 sm:mb-6 bg-gradient-to-r from-primary-600 to-primary-800 bg-clip-text text-transparent">
            Ils Nous Font Confiance
          </h2>
          <p className="text-base sm:text-lg text-neutral-600">
            Découvrez comment nos solutions de mobilité électrique transforment le quotidien de nos clients à travers l'Afrique.
          </p>
        </div>

        {/* Partners Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-8 sm:mb-12 md:mb-16">
          {testimonials.map((testimonial, index) => (
            <div 
              key={index} 
              className={`group relative bg-white rounded-xl shadow-lg overflow-hidden transition-all duration-500 hover:shadow-xl ${
                index === currentIndex ? 'ring-2 ring-primary-500 scale-105' : ''
              }`}
              onClick={() => setCurrentIndex(index)}
            >
              {/* Gradient Background */}
              <div className={`absolute inset-0 bg-gradient-to-br ${testimonial.color} opacity-10 group-hover:opacity-20 transition-opacity duration-500`} />
              
              {/* Logo Container */}
              <div className="relative p-6 sm:p-8">
                <div className="aspect-w-16 aspect-h-9 relative mb-4 sm:mb-6">
                  <img 
                    src={testimonial.logo} 
                    alt={testimonial.company}
                    className="w-full h-24 sm:h-32 object-contain mx-auto filter group-hover:brightness-110 transition-all duration-500 transform group-hover:scale-105"
                  />
                </div>
                
                {/* Industry Badge */}
                <div className="absolute top-3 sm:top-4 right-3 sm:right-4">
                  <span className={`inline-flex items-center px-2 py-1 sm:px-3 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium bg-gradient-to-r ${testimonial.color} text-white shadow-md transform group-hover:scale-105 transition-transform duration-500`}>
                    {testimonial.industry}
                  </span>
                </div>

                {/* Company Name */}
                <div className="text-center">
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1 sm:mb-2 group-hover:text-primary-600 transition-colors duration-300">
                    {testimonial.company}
                  </h3>
                  <div className="flex justify-center">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} size={16} className="text-yellow-400 fill-current" />
                    ))}
                  </div>
                </div>
              </div>

              {/* Hover Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end">
                <div className="p-4 sm:p-6 text-white">
                  <p className="text-sm font-medium mb-1 sm:mb-2">{testimonial.author}</p>
                  <p className="text-xs opacity-80">{testimonial.position}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Testimonial Card */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl p-6 sm:p-8 md:p-12 relative transform transition-all duration-500 hover:shadow-2xl">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-5">
              <div className="absolute inset-0" style={{
                backgroundImage: `radial-gradient(circle at 25% 25%, rgba(0,0,0,0.2) 2%, transparent 2.5%), radial-gradient(circle at 75% 75%, rgba(0,0,0,0.2) 2%, transparent 2.5%)`,
                backgroundSize: '60px 60px'
              }} />
            </div>

            {/* Quote Icon */}
            <div className="absolute top-0 right-0 transform translate-x-4 -translate-y-4 hidden sm:block">
              <Quote size={48} className="text-primary-100" />
            </div>
            
            <div className="relative">
              <blockquote className={`text-lg sm:text-xl md:text-2xl text-gray-800 italic mb-6 sm:mb-8 leading-relaxed transition-opacity duration-500 ${animating ? 'opacity-0' : 'opacity-100'}`}>
                "{testimonials[currentIndex].quote}"
              </blockquote>
              
              <div className="flex items-center">
                <img 
                  src={testimonials[currentIndex].avatar} 
                  alt={testimonials[currentIndex].author} 
                  className="w-12 h-12 sm:w-14 sm:h-14 rounded-full object-cover mr-4 ring-4 ring-primary-50"
                />
                <div>
                  <div className="font-semibold text-gray-900">{testimonials[currentIndex].author}</div>
                  <div className="text-sm text-gray-600">
                    {testimonials[currentIndex].position}, {testimonials[currentIndex].company}
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Navigation Controls */}
          <div className="flex justify-center mt-6 sm:mt-8 space-x-4">
            <button 
              onClick={prevTestimonial}
              className="p-2 rounded-full bg-white border border-gray-200 shadow-sm hover:bg-gray-50 transition-colors group transform hover:scale-110"
              aria-label="Témoignage précédent"
            >
              <ChevronLeft size={20} className="text-gray-600 transition-transform group-hover:-translate-x-1" />
            </button>
            <div className="flex space-x-2 items-center">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-2.5 h-2.5 rounded-full transition-all ${
                    index === currentIndex ? 'bg-primary-600 w-6' : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                  aria-label={`Aller au témoignage ${index + 1}`}
                />
              ))}
            </div>
            <button 
              onClick={nextTestimonial}
              className="p-2 rounded-full bg-white border border-gray-200 shadow-sm hover:bg-gray-50 transition-colors group transform hover:scale-110"
              aria-label="Témoignage suivant"
            >
              <ChevronRight size={20} className="text-gray-600 transition-transform group-hover:translate-x-1" />
            </button>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-8 sm:mt-12 md:mt-16">
          <Link 
            to="/contact" 
            className="inline-flex items-center px-4 sm:px-6 md:px-8 py-2 sm:py-3 md:py-4 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg sm:rounded-xl transition-colors shadow-lg hover:shadow-xl transform hover:translate-y-[-2px] text-sm sm:text-base"
          >
            Rejoindre Nos Partenaires
            <ArrowRight size={18} className="ml-2" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;